// Direct messages: one private, system-owned thread per pair of people
// (DirectMessages::Conversation on the server). The root carries
// thread_type 'dm' and is the pair's alone; the root and every branch under it
// carry dm_user_ids, the pair. A branch also admits people mentioned or
// replying in it, like a private thread, but never lets them up to the root.
//
// Rollout is per-user via the `direct_messages` feature: it gates starting a
// conversation (the profile and hover-card Message buttons) and the Messages
// tab. Reading and replying in a conversation you are part of is never gated.

import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

export const DIRECT_MESSAGES_FEATURE = 'direct_messages'

// Deeper than any real conversation tree; stops a malformed parent chain.
const MAX_CONVERSATION_DEPTH = 20

type DirectMessageQuest =
    | {
          id?: string | number | null
          thread_type?: string | null
          dm_user_ids?: Array<string | number> | null
          dm_request_status?: string | null
          dm_requested_by_id?: string | number | null
          dm_request_sent_at?: string | null
          sorted_answer_ids?: Array<string | number> | null
          sorted_answers?: Array<{ is_draft?: boolean | null }> | null
          parent_id?: string | number | null
          parent_quest_id?: string | number | null
          parent?: { id?: string | number | null; quest_id?: string | number | null } | null
      }
    | null
    | undefined

export function directMessagesEnabled(): boolean {
    return userHasFeature(DIRECT_MESSAGES_FEATURE)
}

// Any thread inside a direct message: the conversation or a branch of it.
export function isDirectMessageQuest(quest: DirectMessageQuest): boolean {
    return Array.isArray(quest?.dm_user_ids) && quest.dm_user_ids.length > 0
}

// The conversation itself, whose head is the system post naming both people.
export function isDirectMessageRoom(quest: DirectMessageQuest): boolean {
    return quest?.thread_type === 'dm' && isDirectMessageQuest(quest)
}

// A thread under the conversation, off one of its messages or deeper.
export function isDirectMessageBranch(quest: DirectMessageQuest): boolean {
    return isDirectMessageQuest(quest) && !isDirectMessageRoom(quest)
}

export function isDirectMessageParticipant(
    quest: DirectMessageQuest,
    viewerId: string | number | null | undefined,
): boolean {
    if (!isDirectMessageQuest(quest) || viewerId == null) return false
    return (quest?.dm_user_ids || []).map(String).includes(String(viewerId))
}

// The other participant, from the viewer's side of the conversation. Someone
// invited into a branch has no side, so null.
export function directMessageCounterpartId(
    quest: DirectMessageQuest,
    viewerId: string | number | null | undefined,
): string | null {
    if (!isDirectMessageParticipant(quest, viewerId)) return null
    const viewer = String(viewerId)
    return (quest?.dm_user_ids || []).map(String).find(id => id !== viewer) || null
}

// The conversation's head: the system post the server writes when the room is
// created. It renders as the other participant rather than as a post.
export function isDirectMessageHeadAnswer(
    answer: { id?: string | number | null } | null | undefined,
    quest: DirectMessageQuest,
): boolean {
    if (answer?.id == null || !isDirectMessageRoom(quest)) return false
    const headId = quest?.parent_id ?? quest?.parent?.id
    return headId != null && String(headId) === String(answer.id)
}

// A conversation that started as a message request to someone who doesn't
// follow the sender, seen from the viewer's side while the recipient hasn't
// accepted it: 'incoming' for the recipient (they accept or decline),
// 'outgoing' for the sender (one message, then wait). Null once it's accepted,
// for conversations that never needed asking, and for branches (the server
// still holds them to the request).
export type DirectMessageRequestSide = 'incoming' | 'outgoing'

export function directMessageRequestSide(
    quest: DirectMessageQuest,
    viewerId: string | number | null | undefined,
): DirectMessageRequestSide | null {
    if (!isDirectMessageRoom(quest) || !isDirectMessageParticipant(quest, viewerId)) return null
    const status = quest?.dm_request_status
    if (status !== 'pending' && status !== 'declined') return null
    if (quest?.dm_requested_by_id == null) return null
    return String(quest.dm_requested_by_id) === String(viewerId) ? 'outgoing' : 'incoming'
}

// Publication spends the allowance even after the message is archived or deleted.
// Answer metadata covers optimistic/older payloads before the timestamp arrives.
export function directMessageRequestHasMessage(quest: DirectMessageQuest): boolean {
    return !!(
        quest?.dm_request_sent_at ||
        quest?.sorted_answer_ids?.length ||
        quest?.sorted_answers?.some(answer => !answer.is_draft)
    )
}

// A thread's ancestors (the read payload's chain, root first) start, inside a
// direct message, with the conversation's head: the system post naming both
// people. The other person stands in for it wherever the chain shows.
export function isDirectMessageConversationHead(
    entry: { is_root?: boolean | null } | null | undefined,
    quest: DirectMessageQuest,
): boolean {
    return !!entry?.is_root && isDirectMessageQuest(quest)
}

export type DirectMessageConversationTarget = {
    questId: string
    // The conversation message the branch grew from; null when unknown.
    answerId: string | null
}

// Where a branch's conversation crumb leads: the conversation thread, at the
// message the branch (or its outermost ancestor branch) grew from. Walks up
// one parent thread per step through `getQuest` (cache first, then the API),
// so a first-level branch resolves after a single read. Null when the chain
// does not reach the conversation, e.g. for someone who cannot read above
// their branch.
export async function resolveDirectMessageConversationTarget(
    quest: DirectMessageQuest,
    getQuest: (questId: string) => Promise<DirectMessageQuest> | DirectMessageQuest,
): Promise<DirectMessageConversationTarget | null> {
    let current = quest
    let anchorAnswerId: string | null = null
    for (let depth = 0; current && depth < MAX_CONVERSATION_DEPTH; depth += 1) {
        if (isDirectMessageRoom(current)) {
            return current.id == null
                ? null
                : { questId: String(current.id), answerId: anchorAnswerId }
        }
        if (!isDirectMessageQuest(current)) return null

        const parentQuestId = current.parent_quest_id ?? current.parent?.quest_id
        if (parentQuestId == null) return null

        const parentAnswerId = current.parent_id ?? current.parent?.id
        anchorAnswerId = parentAnswerId == null ? null : String(parentAnswerId)
        current = await getQuest(String(parentQuestId))
    }
    return null
}

export type RecentDirectMessage = { userId: string; questId: string }

// The people the viewer has written to lately, in inbox order (most recent
// message first), one face per conversation.
export function recentDirectMessageCounterparts(
    quests: DirectMessageQuest[],
    viewerId: string | number | null | undefined,
): RecentDirectMessage[] {
    const seen = new Set<string>()
    const recent: RecentDirectMessage[] = []
    for (const quest of quests || []) {
        if (!isDirectMessageRoom(quest)) continue
        const userId = directMessageCounterpartId(quest, viewerId)
        if (!userId || seen.has(userId)) continue
        seen.add(userId)
        recent.push({ userId, questId: String(quest?.id) })
    }
    return recent
}
